import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppSelector } from "../../../hooks/hooks";
import { selectIsAuthenticated } from "../../../auth/data-access/store/authSlice";
import { useLoginWithMsisdnMutation } from "../../../auth/data-access/store/services/auth.service";
import { decryptMsisdnFromUuid } from "../../../utils/crypto/uuid-decryption";

const HYDRA_PROCESSING_FLAG = "hydra_uuid_processed";
const HYDRA_FALLBACK_FLAG = "hydra_fallback_msisdn_only";
const HYDRA_NO_ACCOUNT_FLAG = "hydra_no_account";

/**
 * Traces the Header Enrichment login, which is worth following because it fails
 * by silently falling back to manual login rather than by erroring.
 *
 * Development only — the browser console is not a private place, so nothing here
 * ships to real visitors, and nothing passed in may include the uuid parameter or
 * the decrypted MSISDN. Which branch was taken is the useful part; the subscriber's
 * number is not.
 */
function trace(...args: Array<unknown>): void {
  if (process.env.NODE_ENV === "production") {
    return;
  }
  // eslint-disable-next-line no-console
  console.log("[HE]", ...args);
}

export interface HydraUuidLoginStatus {
  isProcessing: boolean;
  error: string | null;
}

/**
 * Custom hook that handles Hydra UUID-based automatic login
 *
 * Flow:
 * 1. Checks if uuid parameter exists in URL
 * 2. Skips if user already authenticated or processing already happened
 * 3. Decrypts the MSISDN from the uuid parameter
 * 4. Calls loginWithMsisdn mutation if decryption succeeds
 * 5. On API failure: sets flags and redirects to sign-in to show the subscribe modal
 * 6. Cleans up URL by removing uuid parameter
 *
 * Returns processing status for loading state management
 */
export function useHydraUuidLogin(): HydraUuidLoginStatus {
  const [searchParams] = useSearchParams();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const [loginWithMsisdn] = useLoginWithMsisdnMutation();
  const navigate = useNavigate();
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Check if already authenticated - no need to process
    if (isAuthenticated) {
      return;
    }

    const processHydraUuid = async () => {
      try {
        // Get uuid parameter from URL
        const uuid = searchParams.get("uuid");

        trace("uuid param present:", searchParams.has("uuid"));

        // Check if uuid parameter exists in the URL (even if empty)
        if (!searchParams.has("uuid")) {
          trace("no uuid param in URL, skipping HE flow");
          return;
        }

        // If uuid is empty or missing after we know it was in the URL, treat as fallback
        if (!uuid) {
          trace("uuid param is empty, falling back to manual login");
          sessionStorage.setItem(HYDRA_PROCESSING_FLAG, "true");
          sessionStorage.setItem(HYDRA_FALLBACK_FLAG, "true");
          cleanupUrlParameter();
          return;
        }

        // Check if we already processed a uuid in this session
        const alreadyProcessed = sessionStorage.getItem(HYDRA_PROCESSING_FLAG);
        if (alreadyProcessed) {
          trace("already processed in this session, skipping");
          return;
        }

        // Mark as processing
        setIsProcessing(true);

        trace("attempting to decrypt the uuid param");

        // Decrypt the MSISDN
        const decryptedMsisdn = decryptMsisdnFromUuid(uuid);

        // If decryption failed, show MSISDN-only manual login
        if (!decryptedMsisdn) {
          trace("decryption returned null, falling back to manual login");
          setError("Failed to process authentication");
          // Mark as processed even on failure to avoid loops
          sessionStorage.setItem(HYDRA_PROCESSING_FLAG, "true");
          // Flag to force MSISDN-only login fallback
          sessionStorage.setItem(HYDRA_FALLBACK_FLAG, "true");
          // Clean up URL
          cleanupUrlParameter();
          setIsProcessing(false);
          return;
        }

        trace("decryption succeeded, calling loginWithMsisdn");

        // Mark as processed before calling API (prevent race conditions)
        sessionStorage.setItem(HYDRA_PROCESSING_FLAG, "true");

        // Clean up URL immediately (before API call completes)
        cleanupUrlParameter();

        // Call loginWithMsisdn with decrypted MSISDN
        const loginResult = await loginWithMsisdn({ msisdn: decryptedMsisdn });
        trace("loginWithMsisdn returned an error:", "error" in loginResult);

        if ("error" in loginResult) {
          // API failed: always set fallback flag to prevent infinite redirect to enrichment URL
          sessionStorage.setItem(HYDRA_FALLBACK_FLAG, "true");

          const errorMsg =
            (loginResult.error as { data?: { message?: string } })?.data
              ?.message ?? "";

          const isNoAccount =
            errorMsg.toLowerCase().includes("no account") ||
            errorMsg.toLowerCase().includes("not found");

          trace("login failed:", errorMsg, "| isNoAccount:", isNoAccount);

          if (isNoAccount) {
            // Signal sign-in page to open the subscribe modal
            sessionStorage.setItem(HYDRA_NO_ACCOUNT_FLAG, "true");
          }

          setIsProcessing(false);
          navigate("/auth/sign-in", { replace: true });
          return;
        }

        setIsProcessing(false);
      } catch (err) {
        trace("unexpected error:", err);
        // Safety net: prevent infinite redirect on unexpected errors
        sessionStorage.setItem(HYDRA_FALLBACK_FLAG, "true");
        setError(err instanceof Error ? err.message : "Unknown error");
        setIsProcessing(false);
      }
    };

    processHydraUuid();
  }, [searchParams, isAuthenticated, loginWithMsisdn, navigate]);

  return {
    isProcessing,
    error,
  };
}

/**
 * Removes the uuid parameter from the URL using replaceState
 * Maintains clean browser history
 */
function cleanupUrlParameter(): void {
  try {
    const currentUrl = window.location.href;
    const urlWithoutParams = currentUrl.split("?")[0];
    window.history.replaceState({}, document.title, urlWithoutParams);
  } catch (err) {
    // Silently fail - URL cleanup is not critical
  }
}
